/**
 * @file Approval history and reviewer actions with optional comments.
 */
import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

interface ActionRow {
  id: string;
  action: string;
  level: number;
  comment: string | null;
  createdAt: string;
  actor: string;
}

interface RequestRow {
  id: string;
  status: string;
  currentLevel: number;
  workflow: { name: string | null };
  submittedVersion: { versionNumber: number; title: string };
  actions: ActionRow[];
}

function workflowLabel(name: string | null) {
  const trimmed = name?.trim();
  return trimmed ? trimmed : null;
}

function requestStatusChipProps(status: string): { color: "default" | "primary" | "success" | "warning" | "error" | "info" } {
  switch (status) {
    case "pending":
      return { color: "warning" };
    case "approved":
      return { color: "success" };
    case "rejected":
      return { color: "error" };
    default:
      return { color: "default" };
  }
}

/**
 * Read-only summary for a past approval request inside the history dialog.
 */
function EarlierRequestEntry({ r }: { r: RequestRow }) {
  const chip = requestStatusChipProps(r.status);
  const workflowName = workflowLabel(r.workflow.name);
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          px: 2,
          py: 1.25,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {workflowName ? (
            <Typography variant="subtitle2" fontWeight={600}>
              {workflowName}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: workflowName ? 0.25 : 0 }}>
            v{r.submittedVersion.versionNumber} · level {r.currentLevel}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={r.status.replace(/_/g, " ")}
          color={chip.color}
          variant={chip.color === "default" ? "outlined" : "filled"}
          sx={{ textTransform: "capitalize", flexShrink: 0 }}
        />
      </Box>
      <Box sx={{ px: 2, py: 1.5 }}>
        {r.actions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No actions recorded.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem sx={{ borderStyle: "dashed" }} />} spacing={1.25}>
            {r.actions.map((a) => (
              <Box key={a.id}>
                <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                  <Typography component="span" fontWeight={600}>
                    {a.action}
                  </Typography>{" "}
                  <Typography component="span" variant="body2" color="text.secondary">
                    {a.actor}
                  </Typography>
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                    {new Date(a.createdAt).toLocaleString()}
                  </Typography>
                </Typography>
                {a.comment ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.75,
                      pl: 1.5,
                      borderLeft: 2,
                      borderColor: "divider",
                      fontStyle: "italic",
                    }}
                  >
                    {a.comment}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

function ApprovalRequestCard({
  r,
  pendingRequestId,
  canReview,
  reviewComment,
  onReviewCommentChange,
  onApprove,
  onReject,
}: {
  r: RequestRow;
  pendingRequestId: string | null;
  canReview: boolean;
  reviewComment: string;
  onReviewCommentChange: (v: string) => void;
  onApprove: (id: string, comment?: string) => void | Promise<void>;
  onReject: (id: string, comment?: string) => void | Promise<void>;
}) {
  const workflowName = workflowLabel(r.workflow.name);
  const comment = reviewComment.trim() || undefined;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box>
        {workflowName ? <Typography fontWeight={600}>{workflowName}</Typography> : null}
        <Typography variant="caption" color="text.secondary">
          Version v{r.submittedVersion.versionNumber} · {r.status} · level {r.currentLevel}
        </Typography>
      </Box>
      {canReview && r.status === "pending" && pendingRequestId === r.id ? (
        <Box sx={{ mt: 2, maxWidth: 480 }}>
          <TextField
            size="small"
            fullWidth
            label="Comment (optional)"
            multiline
            minRows={2}
            value={reviewComment}
            onChange={(e) => onReviewCommentChange(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => void onApprove(r.id, comment)}
            >
              Approve
            </Button>
            <Button size="small" variant="outlined" color="error" onClick={() => void onReject(r.id, comment)}>
              Reject
            </Button>
          </Box>
        </Box>
      ) : null}
      <Divider sx={{ my: 2 }} />
      {r.actions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No actions yet.
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {r.actions.map((a) => (
            <Box component="li" key={a.id} sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>{a.action}</strong> by{" "}
                <Typography component="span" variant="body2" sx={{ wordBreak: "break-all" }}>
                  {a.actor}
                </Typography>{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  ({new Date(a.createdAt).toLocaleString()})
                </Typography>
              </Typography>
              {a.comment ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {a.comment}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

/**
 * Shows the latest approval request on the page; earlier requests open in a dialog.
 */
export function ApprovalPanel({
  requests,
  pendingRequestId,
  canReview,
  reviewComment,
  onReviewCommentChange,
  onApprove,
  onReject,
}: {
  requests: RequestRow[];
  pendingRequestId: string | null;
  canReview: boolean;
  reviewComment: string;
  onReviewCommentChange: (v: string) => void;
  onApprove: (id: string, comment?: string) => void | Promise<void>;
  onReject: (id: string, comment?: string) => void | Promise<void>;
}) {
  const [earlierOpen, setEarlierOpen] = useState(false);

  if (requests.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No approval activity yet.
      </Typography>
    );
  }

  // Newest submission first (do not rely on array order or request id — UUIDs are not chronological).
  const sorted = [...requests].sort((a, b) => {
    const d = b.submittedVersion.versionNumber - a.submittedVersion.versionNumber;
    if (d !== 0) {
      return d;
    }
    return b.id.localeCompare(a.id);
  });
  /** Pending review always belongs on the main card, not under "Earlier approvals". */
  const primary =
    pendingRequestId != null
      ? (requests.find((r) => r.id === pendingRequestId) ?? sorted[0])
      : sorted[0];
  const older = sorted.filter((r) => r.id !== primary.id);

  return (
    <Box className="space-y-4">
      <ApprovalRequestCard
        r={primary}
        pendingRequestId={pendingRequestId}
        canReview={canReview}
        reviewComment={reviewComment}
        onReviewCommentChange={onReviewCommentChange}
        onApprove={onApprove}
        onReject={onReject}
      />
      {older.length > 0 ? (
        <>
          <Button
            variant="text"
            color="primary"
            size="small"
            startIcon={<HistoryIcon fontSize="small" />}
            onClick={() => setEarlierOpen(true)}
            sx={{ alignSelf: "flex-start", fontWeight: 500 }}
          >
            View {older.length} earlier approval request{older.length === 1 ? "" : "s"}
          </Button>
          <Dialog
            open={earlierOpen}
            onClose={() => setEarlierOpen(false)}
            fullWidth
            maxWidth="sm"
            scroll="paper"
            slotProps={{
              paper: {
                sx: {
                  height: { xs: "100%", sm: "min(88vh, 640px)" },
                  maxHeight: { xs: "100%", sm: "min(88vh, 640px)" },
                  display: "flex",
                  flexDirection: "column",
                },
              },
            }}
          >
            <DialogTitle
              sx={{
                flexShrink: 0,
                py: 2,
                pr: 1,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <HistoryIcon sx={{ mt: 0.25, color: "action.active", opacity: 0.9 }} fontSize="small" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography component="div" variant="h6" fontWeight={600}>
                    Earlier approvals
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Past review submissions for this page ({older.length}).
                  </Typography>
                </Box>
                <Tooltip title="Close">
                  <IconButton
                    edge="end"
                    color="inherit"
                    aria-label="Close earlier approvals dialog"
                    onClick={() => setEarlierOpen(false)}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </DialogTitle>
            <DialogContent
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                p: 0,
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.06),
              }}
            >
              <Stack spacing={2} sx={{ p: 2 }}>
                {older.map((r) => (
                  <EarlierRequestEntry key={r.id} r={r} />
                ))}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ flexShrink: 0, px: 2.5, py: 2, borderTop: 1, borderColor: "divider" }}>
              <Button variant="contained" onClick={() => setEarlierOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}
    </Box>
  );
}
